import { getCacheLocale, i18nClient } from '@sailor/i18n-web'
import qs from 'qs'
import {
  testEnvHost, AWPENVMAP, HOSTENVMAP,
  testEnvSSOClientId, productEnvSSOClientId, productEnvSSOClientIdOp,
} from '../constants'
import { commonRequest } from '../api'
import { isRtlLocale } from '@sailor/i18n-rtl'
import { getCurLang, getCurRegion } from '@sailor/i18nLocale_m'
import { isEqual, countBy, isEmpty } from 'lodash'

export const REGION_CACHE_KEY = 'region'
export const I18n_PACK_KEY = '3PL_I18NPACKS'

const INIT_I18N_OBJ = {
  region: '',
  regionList: [],
  locale: '',
  localeList: [],
  loginId: '',
}

// 判断两个数组的数组项是否一致，可以乱序对比
export const unorderedEqual = (arr1, arr2) => isEqual(countBy(arr1), countBy(arr2))

const safeParse = (str, defaultVal = {}) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    console.log(e)
    return defaultVal
  }
}

const getQueryValues = (key = '') => {
  const search = qs.parse(location.search, { ignoreQueryPrefix: true })
  if (key) return search[key]
  return search
}

const getComInfoFromSessionStorage = (key) => {
  const infoJson = sessionStorage.getItem(key) || '{}'
  return safeParse(infoJson)
}

const getI18nPacks = () => {
  const s =  getComInfoFromSessionStorage(I18n_PACK_KEY)
  const l = safeParse(localStorage.getItem(I18n_PACK_KEY) || '{}')
  return !isEmpty(s) ? s : l
}


// 系统默认语言，优先级：url参数 > sessionStorage > i18nClient.getCurrentLocale() > 'en'
let defaultLang = ''
if (getQueryValues('locale')) {
  defaultLang = getQueryValues('locale')
} else {
  // @ts-ignore
  defaultLang = getI18nPacks()?.locale ||  getCacheLocale?.() || 'en'
}


// 数据格式：{ locale: 'en', label: 'English' },
let localeInfo = { locale: defaultLang }

// 当前是否RTL环境
const isRTL = () => isRtlLocale(getCacheLocale())

// 判断是否为本地环境
const isLocal = ihost => {
  if (!process.env.AWP_DEPLOY_ENV) return true
  if (ihost) {
    return /^localhost/.test(ihost)
  }
  const { host } = window.top ? window.top.location : window.location
  return /^localhost/.test(host)
}

const isTest = host => testEnvHost.some(testHost => new RegExp(`${testHost}$`).test(host))

const isProd = (includeSt = true, byHost = false) => {
  let prod, st
  if (byHost) {
    const env = getEnv()
    prod = env === 'prod'
    st = env === 'st'
  } else {
    const AWP_DEPLOY_ENV = process.env.AWP_DEPLOY_ENV
    prod = AWP_DEPLOY_ENV === 'production'
    st = AWP_DEPLOY_ENV === 'staging'
  }
  return includeSt ? (prod || st) : prod
}

// return test st prod
const getEnv = (processEnv = {}) => {
  try {
    if (process.env.AWP_DEPLOY_ENV || processEnv.AWP_DEPLOY_ENV) {
      const AWP_DEPLOY_ENV = process.env.AWP_DEPLOY_ENV || processEnv.AWP_DEPLOY_ENV
      return AWPENVMAP[AWP_DEPLOY_ENV] || 'prod'
    }
    return HOSTENVMAP[getHost(true)] || 'prod'
  } catch (e) {
    console.log(e)
    return HOSTENVMAP[getHost(true)] || 'prod'
  }
}


// 获取当前域名
// 注意：local会返回test的host
const getHost = (hostOnly = false) => {
  const { host, protocol } = window.top ? window.top.location : window.location
  const currentHost = isLocal(host) ? testEnvHost[0] : host
  return hostOnly ? currentHost : `${protocol}//${currentHost}`
}

// sso登录地址
const getSSOLoginUrl = () => {
  const { origin, href, host } = window.top ? window.top.location : window.location
  // 登录域名
  const isRunDev = isLocal(host)
  const isTestEnv = isTest(host)
  const sso_host =
    isTestEnv || isRunDev
      ? 'http://ssosv.it.test.sankuai.com/sson'
      : 'https://ssosv.sankuai.com/sson'
  const loginOrigin = `${sso_host}/login`
  const productSSO = getEndpointTypeByKeyword() === ORIGIN_KEY_WORDS.OP_CENTER ? productEnvSSOClientIdOp : productEnvSSOClientId
  const clientId = isTestEnv || isRunDev ? testEnvSSOClientId : productSSO
  const currentPath = href.replace(new RegExp(`^${origin}`), '')
  return `${loginOrigin}?client_id=${clientId}&redirect_uri=${encodeURIComponent(
    // eslint-disable-next-line max-len
    getEndpointTypeByKeyword() === ORIGIN_KEY_WORDS.OP_CENTER ? `${origin}/api/sso/callback?original-url=${encodeURIComponent(currentPath)}` : `${origin}/api/partner/sso/callback?original-url=${encodeURIComponent(currentPath)}`,
  )}`
}

const setLocaleInfo = locale => {
  localeInfo = { locale: getCacheLocale(), ...localeInfo, ...locale }
}

const getLocaleInfo = () => ({ locale: getCacheLocale(), ...localeInfo })


const setCurrentUserInfoToSessionStorage = info => {
  const oldUserInfo = getCurrentUserInfoFromSessionStorage()
  const newUserInfo = { ...oldUserInfo, ...(info || {}) }
  sessionStorage.setItem('PARTNER_USER_INFO', JSON.stringify(newUserInfo))
}
const getCurrentUserInfoFromSessionStorage = () => {
  const infoJson = sessionStorage.getItem('PARTNER_USER_INFO') || '{}'
  return safeParse(infoJson)
}

const ORIGIN_KEY_WORDS = {
  OP_CENTER: 'opcenter',
  COURIER: 'courier',
}

const getEndpointTypeByKeyword = () => {
  const domain = window.location.origin
  const keywordMap = {
    'opcenter': 'opcenter',
    'courier': 'courier',
    'localhost': 'courier',
  }
  for (const [keyword, endpoint] of Object.entries(keywordMap)) {
    if (domain.includes(keyword)) {
      return endpoint
    }
  }
  return 'opcenter'
}

const isBOC = getEndpointTypeByKeyword() === ORIGIN_KEY_WORDS.OP_CENTER

const getI18nParams = () => {
  let currentRegion
  if (getEndpointTypeByKeyword() === ORIGIN_KEY_WORDS.OP_CENTER) {
    currentRegion = getCurRegion()?.code
  } else {
    // window.__initRegion 在主框架 HTML 中注入，courier 作为 iframe 子应用时该值可能未继承
    // 依次从 window.__initRegion -> sessionStorage -> localStorage 读取，保证在任何时机都能拿到正确的 region
    currentRegion = window?.__initRegion
      || sessionStorage.getItem('region')
      || localStorage.getItem('region')
      || safeParse(sessionStorage.getItem('PARTNER_USER_INFO'), {})?.region
      || new URLSearchParams(window?.location?.search)?.get('region')
  }
  const currentLocale = getEndpointTypeByKeyword() === ORIGIN_KEY_WORDS.OP_CENTER ? getLanguage() : getLocaleInfo().locale || 'en'
  return {
    locale: currentLocale,
    region: currentRegion,
  }
}

// 权限相关
// 按钮功能权限判断，在uac配置对应接口url作为key，或纯文本key，并关联权限
function hasPermission (key) {
  if (isLocal()) return true
  return window.top._hasPermission && window.top._hasPermission(key)
}

function getQuery () {
  const [host, search] = location.href.split('?')
  if (!search) return {}
  return search.split('&').reduce((dist, item) => {
    const [key, value] = item.split('=')
    return { ...dist, [key]: value }
  }, {})
}

// 获取语言
const getLanguage = () => (i18nClient.isInitialized ? i18nClient.getCurrentLocale() : getCacheLocale()) || 'en'

const isPtBR = getI18nParams().locale === 'pt-BR'
const isBR = getI18nParams().region === 'BR'

const syncStorage = (key, obj) => {
  sessionStorage.setItem(key, obj)
  localStorage.setItem(key, obj)
}

const setI18nPacks = (obj = {}) => {
  const packs = getComInfoFromSessionStorage(I18n_PACK_KEY)
  const packsFromLocalStorage = safeParse(localStorage.getItem(I18n_PACK_KEY))
  syncStorage(I18n_PACK_KEY, JSON.stringify({ ...INIT_I18N_OBJ, ...packsFromLocalStorage, ...packs, ...obj }))
}

const syncRegion = (obj, cb) => {
  const { region } = obj
  setI18nPacks(obj)
  if (region) {
    cb?.(region)
    syncStorage(REGION_CACHE_KEY, region)
    setCurrentUserInfoToSessionStorage({ region }) // 子应用region可能从getCurrentUserInfoFromSessionStorage取值，这里为了避免子应用在壳子升级之后未重新部署升级，做的兼容
    syncStorage('region', region) // 同上，消息中心region从SessionStorege拿
    window.__initRegion = region
    window.__process_env_appkey = window.__initRegion === 'SA' ? 'com.sankuai.sailorfe.d.pc' : 'com.keetapp.sailorfe.d.pc'
  }
}


const syncLocale = (obj, cb) => {
  const { locale } = obj
  setI18nPacks(obj)
  if (locale) {
    cb?.(locale)
    sessionStorage.setItem('i18nLocale', locale)
    syncStorage('i18nLocale', locale)
    if (getLanguage() !== locale) {
      changeI18nClientLanguage(locale)
    }
  }
}

const changeI18nClientLanguage = locale => {
  i18nClient.changeLanguage(locale, (err) => {
    if (err) {
      console.error('语言切换失败', locale)
      return
    }
    // 获取切换后的文案
    console.log('opcenter_langauge', locale)
  })
}


/**
 * @description 修正localStorage的regionPacks，确保与sessionStorage一致
 * 确保当前页面新开tab场景region始终一致
 */
const syncLocalStorageWithSession = () => {
  const sessionPacks = getComInfoFromSessionStorage(I18n_PACK_KEY)
  let packs = localStorage.getItem(I18n_PACK_KEY)
  packs = JSON.parse(packs || '{}')
  // 初始登陆/登陆新设备，没有sessionPacks；如果不是同个账号，不执行逻辑
  if (!sessionPacks) return
  if (packs?.region !== sessionPacks?.region || packs?.locale !== sessionPacks?.locale) {
    syncStorage(I18n_PACK_KEY, JSON.stringify(sessionPacks))
  }
}

// 清除缓存
const removePartnerInfoCache = () => {
  sessionStorage.removeItem('LOGIN_PARTNERID')
  sessionStorage.removeItem('cityId')
  // 清除region和compassRegion缓存
  sessionStorage.removeItem('compassRegion')
  sessionStorage.removeItem('region')
  localStorage.removeItem('compassRegion')
  localStorage.removeItem('region')
}

const toSSOLogin = () => {
  removePartnerInfoCache()
  window.top ? (window.top.location.href = getSSOLoginUrl()) : (location.href = getSSOLoginUrl()) // 默认登出后再登录是重定向到应用首页
}

// https://km.sankuai.com/collabpage/1463829017
const logout = (url) => {
  commonRequest.get('/api/partner/sso/logout').finally(() => {
    toSSOLogin()
  })
}

export {
  hasPermission,
  isLocal,
  isTest,
  isProd,
  getEnv,
  logout,
  getHost,
  getSSOLoginUrl,
  setLocaleInfo,
  getLocaleInfo,
  getI18nParams,
  getQueryValues,
  getQuery,
  safeParse,
  toSSOLogin,
  isRTL,
  setCurrentUserInfoToSessionStorage,
  getCurrentUserInfoFromSessionStorage,
  getComInfoFromSessionStorage,
  getLanguage,
  getEndpointTypeByKeyword,
  ORIGIN_KEY_WORDS,
  isPtBR,
  isBR,
  defaultLang,
  syncLocale,
  syncRegion,
  removePartnerInfoCache,
  syncLocalStorageWithSession,
  changeI18nClientLanguage,
  setI18nPacks,
  isBOC
}
